import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Grid, Flex, Box, Copy, Button } from '@elparking/components'
import { FormattedMessage } from 'react-intl'
import ExtraServiceBox from '../ExtraServiceBox'
import Resume from '../Resume'
import ResumeContainer from '../Resume/ResumeContainer'
import BottomBar from '../BottomBar'
import { RentalCarEvents, trackTealiumEvent } from 'commons/js/services/analytics'
import scrollTo from 'commons/js/util/scrollTo'
import LoadingBlock from 'commons/js/ui/LoadingBlock'
import { isWebview } from '@elparking/utils'
import { STEP_4 } from 'rental-car-contract/js/constants'

const Step3 = ({
    requestUpdateBooking,
    token,
    extras,
    fixedCoverages,
    coverages,
    id,
    loading,
    onNext,
    onBack,
    scrollTo,
    isBuildBookingLoading,
    setOnCloseModal,
    lastStep,
    isWebview,
}) => {
    const onUpdate = useCallback(
        () => {
            requestUpdateBooking({token, id, extras, fixedCoverages, coverages})
        },
        [requestUpdateBooking, token, id, extras, fixedCoverages, coverages]
    )

    useEffect(() => {
        trackTealiumEvent(RentalCarEvents.showExtrasView)
    }, [])

    useEffect(() => {
        scrollTo(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (loading || isBuildBookingLoading || !extras || !coverages) {
        return <LoadingBlock width='100%' size='xxxLarge'/>
    }

    const onExtraChanged = (extra, qtySelected, arr) => {
        arr.forEach(c => {
            if ((c.id === extra.id) && (qtySelected !== c.qtySelected)) {
                qtySelected > c.qtySelected && trackTealiumEvent(RentalCarEvents.addExtraLink)
                c.qtySelected = qtySelected
                onUpdate()
            }
        })
    }

    if (!loading && !isBuildBookingLoading && extras.length === 0 && fixedCoverages.length === 0) {
        (isWebview() && lastStep === STEP_4) ? onBack() : onNext()
    }
    return (
        <Box width='100%' paddingBottom={['90px', '0px']}>
            <Flex padding={['0', '0', '2em 0em']} alignItems='flex-start' flexDirection={['column', 'row']}>
                <Box width={['100%', 'calc(100% - 330px)']} padding={['1em', '0em']}>
                    <Copy as='h1' fontSize={['large', 'xxLarge']} fontColor='accent_dark' padding='0'><FormattedMessage defaultMessage='Elige tus extras'/></Copy>
                    <Box>
                        {fixedCoverages.length > 0 && <>
                            <Copy as='h2' fontSize={['small', 'xLarge']} fontColor='darkGray' padding='1em 0em'><FormattedMessage defaultMessage='Coberturas' /></Copy>
                            <Flex gridGap='1em' flexDirection={['column', 'row']} flexWrap='wrap'>
                                {fixedCoverages.map(extra => <Box width={['100%', 'calc(50% - 0.5em)']} key={`coverage_${extra.id}`} padding='0'>
                                    <ExtraServiceBox
                                      title={extra.name}
                                      subtitle={extra.subName}
                                      description={extra.description}
                                      icon={extra.logoUrl}
                                      price={extra.displayCustomerPrice}
                                      defaultUnits={fixedCoverages.qtySelected}
                                      included={extra.includedInEstTotalInd}
                                      qtyMax={extra.qtyMax}
                                      onChange={qty => onExtraChanged(extra, qty, fixedCoverages)}
                                      squared
                                    />
                                </Box>)}
                                {coverages.map(extra => <Box width={['100%', 'calc(50% - 0.5em)']} key={`coverage_${extra.id}`} padding='0'>
                                    <ExtraServiceBox
                                      title={extra.name}
                                      subtitle={extra.subName}
                                      description={extra.description}
                                      icon={extra.logoUrl}
                                      price={extra.displayCustomerPrice}
                                      defaultUnits={extra.qtySelected}
                                      qtyMax={extra.qtyMax}
                                      onChange={qty => onExtraChanged(extra, qty, coverages)}
                                      squared
                                    />
                                </Box>)}
                            </Flex>
                        </>}
                        {extras.length > 0 && <><Copy as='h2' fontSize={['small', 'xLarge']} fontColor='darkGray' padding='1em 0em'><FormattedMessage defaultMessage='Extras'/></Copy>
                            <Grid gridGap='1em' gridTemplateColumns={['1fr 1fr', '1fr']}>
                                {extras.map(extra => <Flex height='100%' width='100%' key={`extra_box_${extra.id}`}>
                                    <ExtraServiceBox
                                      title={extra.name}
                                      subtitle={extra.subName}
                                      description={extra.description}
                                      icon={extra.logoUrl}
                                      price={extra.displayCustomerPrice}
                                      defaultUnits={extra.qtySelected}
                                      highlighted={extra.highlighted}
                                      qtyMax={extra.qtyMax}
                                      onChange={qty => onExtraChanged(extra, qty, extras)}
                                />
                                </Flex>)}
                            </Grid></>}
                    </Box>
                    <Box textAlign='right' mt='2em' display={['none', 'none', 'block']}>
                        <Button onClick={onNext} height='auto' padding={['12px 20px']} fontWeight='bold'>
                            <FormattedMessage defaultMessage='Continuar' />
                        </Button>
                    </Box>
                </Box>
                <Box display={['none', 'none', 'block']} width={['100%', '100%', '330px']} marginLeft={['0', '0', '1em']}>
                    <Flex width='100%' flexDirection='column'>
                        <ResumeContainer>
                            <Resume />
                        </ResumeContainer>
                    </Flex>
                </Box>
            </Flex>
            <BottomBar setOnCloseModal={setOnCloseModal} display={['block', 'none']} disabled={loading} onNext={onNext} />
        </Box>
    )
}

Step3.propTypes = {
    id: PropTypes.any,
    loading: PropTypes.bool,
    coverages: PropTypes.array,
    fixedCoverages: PropTypes.array,
    extras: PropTypes.array,
    onNext: PropTypes.func,
    requestUpdateBooking: PropTypes.func,
    setOnCloseModal: PropTypes.func,
    scrollTo: PropTypes.func,
    token: PropTypes.string,
    isBuildBookingLoading: PropTypes.bool,
    lastStep: PropTypes.number,
    isWebview: PropTypes.func,
    onBack: PropTypes.func,
}

Step3.defaultProps = {
    setOnCloseModal: () => {},
    requestUpdateBooking: () => {},
    onNext: () => {},
    scrollTo,
    isWebview,
}

export default Step3
